import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Activity, Eye, Wifi, AlertTriangle, ChevronRight, Clock, MapPin, Monitor } from 'lucide-react';

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const roleColors = {
  admin: 'bg-violet-100 text-violet-700',
  coordinator: 'bg-sky-100 text-sky-700',
  facilitator: 'bg-amber-100 text-amber-700',
  client: 'bg-emerald-100 text-emerald-700',
  housing_resident: 'bg-teal-100 text-teal-700',
  peer_specialist: 'bg-rose-100 text-rose-700',
};

function prettyPath(p) {
  // Make API paths human-friendly
  return p
    .replace(/^\/api\//, '')
    .replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '/{id}')
    .replace(/\/[0-9a-f]{24}/g, '/{id}');
}

export default function LiveActivityFeed() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, sessRes] = await Promise.all([
        api.get('/activity/summary?hours=24'),
        api.get('/activity/sessions?minutes=30'),
      ]);
      setSummary(sumRes.data);
      setSessions(sessRes.data.sessions || []);
    } catch {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 15000); // refresh every 15s
    return () => clearInterval(t);
  }, [fetchData]);

  const openUserDetail = async (userId) => {
    setDetailLoading(true);
    setDetail({ loading: true });
    try {
      const res = await api.get(`/activity/user/${userId}?limit=200`);
      setDetail(res.data);
    } catch {
      setDetail(null);
    }
    setDetailLoading(false);
  };

  return (
    <>
      <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-emerald-400" data-testid="live-activity-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Activity size={16} className="text-emerald-600" />
            Live Activity
            <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] ml-2 animate-pulse">LIVE</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">Last 24h · Auto-refresh every 15s · Tap a user to see every page they visited</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Top stats */}
          {summary && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-emerald-700">{summary.total_requests}</p>
                <p className="text-xs text-emerald-600">Requests (24h)</p>
              </div>
              <div className="bg-sky-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-sky-700">{summary.unique_users}</p>
                <p className="text-xs text-sky-600">Unique Users</p>
              </div>
              <div className="bg-rose-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-rose-700">{sessions.length}</p>
                <p className="text-xs text-rose-600">Active Now</p>
              </div>
            </div>
          )}

          {/* Active sessions */}
          {sessions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Wifi size={11} /> Active Right Now
              </p>
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {sessions.map(s => (
                  <button
                    key={s.user_id}
                    onClick={() => openUserDetail(s.user_id)}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 transition-colors text-left"
                    data-testid={`active-session-${s.user_id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">
                      {s.user_email?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{s.user_email}</p>
                      <p className="text-[11px] text-slate-500 flex items-center gap-2">
                        <span className="flex items-center gap-0.5"><MapPin size={9} /> {s.ip}</span>
                        <span>·</span>
                        <span>{s.request_count} reqs</span>
                        <span>·</span>
                        <span className="text-emerald-600">{timeAgo(s.last_active)}</span>
                      </p>
                    </div>
                    <Badge className={`${roleColors[s.user_role] || 'bg-slate-100 text-slate-600'} border-0 text-[10px]`}>{s.user_role}</Badge>
                    <ChevronRight size={14} className="text-slate-300 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* All users by activity */}
          {summary?.by_user?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">All Users — Last 24h</p>
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {summary.by_user.slice(0, 20).map(u => (
                  <button
                    key={u.user_id}
                    onClick={() => openUserDetail(u.user_id)}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 text-left"
                    data-testid={`activity-user-${u.user_id}`}
                  >
                    <Eye size={12} className="text-slate-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{u.user_email}</p>
                      <p className="text-[10px] text-slate-500">
                        {u.request_count} requests · {u.unique_path_count} pages · last {timeAgo(u.last_active)}
                      </p>
                    </div>
                    {u.errors > 0 && (
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[9px]"><AlertTriangle size={8} className="mr-0.5" /> {u.errors}</Badge>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Activity size={18} /> Activity Log</DialogTitle>
            <DialogDescription>{detail?.user?.full_name} · {detail?.user?.email}</DialogDescription>
          </DialogHeader>

          {detailLoading || detail?.loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="w-7 h-7 border-3 border-emerald-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : detail && detail.activity && (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold">{detail.stats.total_requests_all_time}</p>
                  <p className="text-[11px] text-slate-500">All-time</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{detail.stats.requests_last_24h}</p>
                  <p className="text-[11px] text-emerald-600">Last 24h</p>
                </div>
                <div className="bg-sky-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-sky-700">{detail.stats.rows_returned}</p>
                  <p className="text-[11px] text-sky-600">Shown</p>
                </div>
              </div>
              <ScrollArea className="max-h-[55vh]">
                <div className="space-y-1 pr-3">
                  {detail.activity.map((a, idx) => (
                    <div key={`${a.timestamp}-${idx}`} className="flex items-start gap-2 p-2 hover:bg-slate-50 rounded text-xs border-b border-slate-100">
                      <Badge className={`${a.status_code >= 400 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'} border-0 text-[9px] font-mono shrink-0`}>
                        {a.method}
                      </Badge>
                      <code className="font-mono text-slate-700 flex-1 truncate">{prettyPath(a.path)}</code>
                      <span className="text-slate-400 shrink-0">{a.status_code}</span>
                      <span className="text-slate-400 shrink-0">{a.duration_ms}ms</span>
                      <span className="text-slate-500 shrink-0 flex items-center gap-0.5">
                        <Clock size={9} /> {timeAgo(a.timestamp)}
                      </span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
